import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Autocomplete,
  Box,
  Chip,
  Alert,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { Save, Cancel, CloudUpload, PictureAsPdf, Delete, Close } from '@mui/icons-material';
import SuccessModal from './SuccessModal';
import ErrorModal from './ErrorModal';

const TIPOS_DOCUMENTOS = [
  { value: 'cpp', label: '1. Certificados CPP', requiresExpiry: false },
  { value: 'gmp', label: '2. GMP (ISO, OMS, FDA, EMA)', requiresExpiry: false },
  { value: 'carta_autorizacion', label: '3. Carta de Autorización', requiresExpiry: false },
  { value: 'carta_distribucion', label: '4. Carta de Distribución', requiresExpiry: false },
  { value: 'carta_poder', label: '5. Carta Poder', requiresExpiry: false },
  { value: 'ficha_tecnica', label: '6. Fichas Técnicas', requiresExpiry: false },
  { value: 'hoja_seguridad', label: '7. Hojas de Seguridad', requiresExpiry: false },
  { value: 'monografia', label: '8. Monografías', requiresExpiry: false },
  { value: 'certificado_calidad', label: '9. Certificados de Calidad', requiresExpiry: false },
  { value: 'etiquetas', label: '10. Etiquetas (1ª, 2ª, 3ª)', requiresExpiry: false },
  { value: 'estudio_clinico', label: '11. Estudios Clínicos', requiresExpiry: false },
  { value: 'bioequivalencia', label: '12. Estudios de Bioequivalencia', requiresExpiry: false },
];

const ProductoForm = ({ producto, onClose }) => {
  const [formData, setFormData] = useState({
    registro_sanitario_id: '',
    nombre: '',
    descripcion_presentacion: '',
    codigo_sap: '',
    laboratorio_fabricante: '',
    categoria: 'No controlado',
    vida_util: '',
    pais_origen: '',
    codigo_proveedor: '',
    es_descartable: false,
    estado: 'activo',
  });

  const [registrosSanitarios, setRegistrosSanitarios] = useState([]);
  const [selectedRegistro, setSelectedRegistro] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({
    title: '',
    message: '',
    type: 'create'
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({
    title: '',
    message: '',
    type: 'error',
    details: null
  });
  const [imagenes, setImagenes] = useState({
    imagen_1: null,
    imagen_2: null,
    imagen_3: null,
    imagen_4: null,
  });
  const [imagenesPreviews, setImagenesPreviews] = useState({
    imagen_1: null,
    imagen_2: null,
    imagen_3: null,
    imagen_4: null,
  });

  // Estado para documentos
  const [documentos, setDocumentos] = useState({});
  const [documentosFechas, setDocumentosFechas] = useState({});
  const [documentosAEliminar, setDocumentosAEliminar] = useState([]);

  // Estado para lotes (número de lote y fecha de vencimiento)
  const [lotes, setLotes] = useState({
    lote_1: '',
    lote_2: '',
    lote_3: '',
    lote_4: '',
  });
  const [loteFechas, setLoteFechas] = useState({
    fecha_lote_1: '',
    fecha_lote_2: '',
    fecha_lote_3: '',
    fecha_lote_4: '',
  });

  // Estado para imágenes a eliminar
  const [imagenesAEliminar, setImagenesAEliminar] = useState([]);

  // Estado para certificados de análisis (PDFs por lote)
  const [certificadosAnalisis, setCertificadosAnalisis] = useState({
    cert_1: null,
    cert_2: null,
    cert_3: null,
    cert_4: null,
  });

  // Estado para rastrear certificados eliminados
  const [certificadosEliminados, setCertificadosEliminados] = useState({
    cert_1: false,
    cert_2: false,
    cert_3: false,
    cert_4: false,
  });

  // Cargar registros sanitarios solo una vez al montar el componente
  useEffect(() => {
    loadRegistrosSanitarios();
  }, []); // Sin dependencias = solo se ejecuta una vez

  // Manejar cambios en el producto (edición)
  useEffect(() => {
    if (producto) {
      setFormData({
        registro_sanitario_id: producto.sanitaryRegistryId || producto.registro_sanitario_id || '',
        nombre: producto.name || producto.nombre || '',
        descripcion_presentacion: producto.description || producto.descripcion_presentacion || '',
        codigo_sap: producto.sapCode || producto.codigo_sap || '',
        laboratorio_fabricante: producto.manufacturer || producto.laboratorio_fabricante || '',
        categoria: producto.category || producto.categoria || 'No controlado',
        vida_util: producto.shelfLife || producto.vida_util || '',
        pais_origen: producto.originCountry || producto.pais_origen || '',
        codigo_proveedor: producto.providerCode || producto.codigo_proveedor || '',
        es_descartable: producto.isDisposable === 1 || producto.isDisposable === true || producto.es_descartable === 1 || producto.es_descartable === true,
        estado: producto.status || producto.estado || 'activo',
      });

      // Cargar previews de imágenes existentes
      const newImagenesPreviews = {};
      for (let i = 1; i <= 4; i++) {
        const imagenKey = `imagen_${i}`;
        const imageKeyEnglish = `image${i}`;
        if (producto[imagenKey] || producto[imageKeyEnglish]) {
          newImagenesPreviews[imagenKey] = producto[imagenKey] || producto[imageKeyEnglish];
        }
      }
      setImagenesPreviews(newImagenesPreviews);

      // Cargar datos de lotes existentes (NUEVA ESTRUCTURA: lot1Number, lot1ExpiryDate)
      setLotes({
        lote_1: producto.lot1Number || producto.lot1 || '',
        lote_2: producto.lot2Number || producto.lot2 || '',
        lote_3: producto.lot3Number || producto.lot3 || '',
        lote_4: producto.lot4Number || producto.lot4 || '',
      });

      // Cargar fechas de vencimiento de lotes
      setLoteFechas({
        fecha_lote_1: producto.lot1ExpiryDate || '',
        fecha_lote_2: producto.lot2ExpiryDate || '',
        fecha_lote_3: producto.lot3ExpiryDate || '',
        fecha_lote_4: producto.lot4ExpiryDate || '',
      });

      // Cargar fechas de vencimiento de documentos
      const fechasDocumentos = {};
      for (let i = 1; i <= 12; i++) {
        const tipoDoc = TIPOS_DOCUMENTOS[i - 1]; // Obtener el tipo de documento
        if (tipoDoc) {
          const key = `global-${tipoDoc.value}`;
          const expiryDate = producto[`document${i}ExpiryDate`];
          if (expiryDate) {
            fechasDocumentos[key] = expiryDate;
          }
        }
      }
      setDocumentosFechas(fechasDocumentos);

      // Nota: Los documentos existentes se mostrarán mediante el campo nombre_documento_X
      // No creamos objetos File porque solo necesitamos mostrar que existen

      if (producto.registro_sanitario_id && registrosSanitarios.length > 0) {
        const registro = registrosSanitarios.find(r => r.id === producto.registro_sanitario_id);
        if (registro) {
          setSelectedRegistro(registro);
        }
      }
    }
  }, [producto, registrosSanitarios]); // Incluir registrosSanitarios para cargar el registro seleccionado

  const loadRegistrosSanitarios = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/registros-sanitarios?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrosSanitarios(data.registros);
      }
    } catch (error) {
      console.error('Error loading registros sanitarios:', error);
    }
  };

  // Cleanup de URLs de vistas previas cuando se desmonte el componente
  useEffect(() => {
    return () => {
      // Liberar memoria de las URLs creadas con createObjectURL
      Object.values(imagenesPreviews).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagenesPreviews]);

  const handleRegistroChange = (event, value) => {
    setSelectedRegistro(value);
    if (value) {
      setFormData({
        ...formData,
        registro_sanitario_id: value.id,
        nombre: value.producto_nombre || formData.nombre,
      });
    } else {
      setFormData({
        ...formData,
        registro_sanitario_id: '',
      });
    }
  };

  const handleSubmit = async () => {
    try {
      // Validar que el nombre del producto no esté vacío
      if (!formData.nombre || formData.nombre.trim() === '') {
        setError('El nombre del producto es obligatorio');
        return;
      }
      const token = localStorage.getItem('token');

      const url = producto
        ? `/api/products/${producto.id}`
        : `/api/products`;

      const method = producto ? 'PUT' : 'POST';

      // Mapeo de campos español -> inglés para la API
      const fieldMapping = {
        nombre: 'name',
        descripcion_presentacion: 'description',
        codigo_sap: 'sapCode',
        laboratorio_fabricante: 'manufacturer',
        categoria: 'category',
        vida_util: 'shelfLife',
        pais_origen: 'originCountry',
        codigo_proveedor: 'providerCode',
        es_descartable: 'isDisposable',
        estado: 'status',
        registro_sanitario_id: 'sanitaryRegistryId'
      };

      // Detectar si hay archivos nuevos
      let hasNewFiles = false;
      for (let i = 1; i <= 4; i++) {
        if (imagenes[`imagen_${i}`]) {
          hasNewFiles = true;
          break;
        }
      }
      if (!hasNewFiles) {
        for (let i = 1; i <= 4; i++) {
          if (certificadosAnalisis[`cert_${i}`]) {
            hasNewFiles = true;
            break;
          }
        }
      }
      if (!hasNewFiles) {
        for (const key in documentos) {
          if (documentos[key]) {
            hasNewFiles = true;
            break;
          }
        }
      }

      let requestBody;
      let requestHeaders = {
        'Authorization': `Bearer ${token}`,
      };

      // Si NO hay archivos nuevos, usar JSON
      if (!hasNewFiles) {
        const jsonData = {};
        Object.keys(formData).forEach(key => {
          if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
            const apiFieldName = fieldMapping[key] || key;
            jsonData[apiFieldName] = formData[key];
          }
        });

        // Agregar lotes (NUEVA ESTRUCTURA: lot1Number, lot1ExpiryDate, lot1AnalysisCert)
        for (let i = 1; i <= 4; i++) {
          const loteNum = lotes[`lote_${i}`];
          const loteFecha = loteFechas[`fecha_lote_${i}`];
          const certificadoEliminado = certificadosEliminados[`cert_${i}`];

          if (loteNum) {
            jsonData[`lot${i}Number`] = loteNum;
            jsonData[`lot${i}`] = loteNum; // Mantener compatibilidad
          }
          if (loteFecha) {
            jsonData[`lot${i}ExpiryDate`] = loteFecha;
          }
          // Si se marcó como eliminado, enviar null para eliminarlo
          if (certificadoEliminado) {
            jsonData[`lot${i}AnalysisCert`] = null;
          }
        }

        // Agregar fechas de vencimiento de documentos
        for (let i = 1; i <= 12; i++) {
          const tipoDoc = TIPOS_DOCUMENTOS[i - 1];
          if (tipoDoc) {
            const key = `global-${tipoDoc.value}`;
            const fechaVencimiento = documentosFechas[key];
            if (fechaVencimiento) {
              jsonData[`document${i}ExpiryDate`] = fechaVencimiento;
            }
          }
        }

        // Agregar imágenes a eliminar
        for (let i = 1; i <= 4; i++) {
          const imageKey = `imagen_${i}`;
          if (imagenesAEliminar.includes(imageKey)) {
            jsonData[`image${i}`] = null;
          }
        }

        // Agregar documentos a eliminar
        if (producto && documentosAEliminar.length > 0) {
          jsonData.deleteDocuments = JSON.stringify(documentosAEliminar);
        }

        requestHeaders['Content-Type'] = 'application/json';
        requestBody = JSON.stringify(jsonData);
      } else {
        // Si hay archivos, usar FormData
        const formDataToSend = new FormData();

        Object.keys(formData).forEach(key => {
          if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
            const apiFieldName = fieldMapping[key] || key;
            formDataToSend.append(apiFieldName, formData[key]);
          }
        });

        // Agregar imágenes
        for (let i = 1; i <= 4; i++) {
          const imageKey = `imagen_${i}`;
          const imagen = imagenes[imageKey];

          // Si hay una imagen nueva, subirla
          if (imagen) {
            formDataToSend.append(`image${i}`, imagen);
          }
          // Si se marcó para eliminar, enviar cadena vacía
          else if (imagenesAEliminar.includes(imageKey)) {
            formDataToSend.append(`image${i}`, '');
          }
        }

        // Agregar lotes (NUEVA ESTRUCTURA: lot1Number, lot1ExpiryDate, lot1AnalysisCert)
        for (let i = 1; i <= 4; i++) {
          const loteNum = lotes[`lote_${i}`];
          const loteFecha = loteFechas[`fecha_lote_${i}`];
          const certificado = certificadosAnalisis[`cert_${i}`];
          const certificadoEliminado = certificadosEliminados[`cert_${i}`];

          if (loteNum) {
            formDataToSend.append(`lot${i}Number`, loteNum);
            formDataToSend.append(`lot${i}`, loteNum); // Mantener compatibilidad
          }
          if (loteFecha) {
            formDataToSend.append(`lot${i}ExpiryDate`, loteFecha);
          }

          // Si hay un certificado nuevo, subirlo
          if (certificado) {
            formDataToSend.append(`lot${i}AnalysisCert`, certificado);
          }
          // Si se marcó como eliminado, enviar cadena vacía para eliminarlo
          else if (certificadoEliminado) {
            formDataToSend.append(`lot${i}AnalysisCert`, '');
          }
        }

        // Agregar documentos GLOBALES (1-12)
        for (const key in documentos) {
          if (key.startsWith('global-')) {
            const tipoDocumento = key.replace('global-', '');
            const file = documentos[key];
            const fechaVencimiento = documentosFechas[key] || null;

            if (file) {
              const tipoDocIndex = TIPOS_DOCUMENTOS.findIndex(t => t.value === tipoDocumento);
              if (tipoDocIndex !== -1) {
                const docNumber = tipoDocIndex + 1; // 1-12
                formDataToSend.append(`document${docNumber}`, file);

                if (fechaVencimiento) {
                  formDataToSend.append(`document${docNumber}ExpiryDate`, fechaVencimiento);
                }
              }
            }
          } else {
            // DOCUMENTOS VIEJOS (1-4 por imagen = 1-48) - Mantener compatibilidad temporal
            const [imagenNumero, tipoDocumento] = key.split('-');
            if (imagenNumero && tipoDocumento) {
              const file = documentos[key];
              const fechaVencimiento = documentosFechas[key] || null;

              if (file) {
                const tipoDocIndex = TIPOS_DOCUMENTOS.findIndex(t => t.value === tipoDocumento);
                if (tipoDocIndex !== -1) {
                  const docNumber = ((parseInt(imagenNumero) - 1) * 12) + (tipoDocIndex + 1);
                  formDataToSend.append(`document${docNumber}`, file);

                  if (fechaVencimiento) {
                    formDataToSend.append(`document${docNumber}ExpiryDate`, fechaVencimiento);
                  }
                }
              }
            }
          }
        }

        // Agregar documentos a eliminar
        if (producto && documentosAEliminar.length > 0) {
          formDataToSend.append('deleteDocuments', JSON.stringify(documentosAEliminar));
        }

        requestBody = formDataToSend;
        // NO incluir Content-Type para FormData - el navegador lo configura automáticamente
      }

      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: requestBody,
      });

      if (response.ok) {
        const savedProduct = await response.json();

        // Mostrar modal de éxito super cool
        setSuccessModalData({
          title: producto ? '¡Producto Actualizado!' : '¡Producto Creado!',
          message: producto
            ? `El producto "${formData.nombre}" ha sido actualizado exitosamente.`
            : `El producto "${formData.nombre}" ha sido creado y está listo para usar.`,
          type: producto ? 'update' : 'create'
        });
        setShowSuccessModal(true);

        // Cerrar el formulario después de que se cierre el modal
        setTimeout(() => {
          onClose();
        }, 3600);
      } else {
        // Si es 401, el token expiró - redirigir al login
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setError('Sesión expirada. Por favor inicia sesión nuevamente.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }

        // Intentar parsear el error del servidor
        let errorMessage = 'Error al guardar producto';
        let errorDetails = null;

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Si no se puede parsear JSON, usar el statusText
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }

        // Determinar tipo de error y detalles
        if (response.status === 413) {
          setErrorModalData({
            title: '❌ Archivos Demasiado Grandes',
            message: 'El tamaño total de los archivos excede el límite permitido.',
            type: 'error',
            details: 'El límite máximo es de 200MB por solicitud.\n\nRecomendaciones:\n• Comprime las imágenes antes de subirlas\n• Reduce la calidad de los PDFs\n• Sube los archivos en varias actualizaciones\n• Tamaño ideal por imagen: 3-5 MB\n• Tamaño ideal por PDF: 5-10 MB'
          });
        } else if (response.status === 400) {
          setErrorModalData({
            title: '⚠️ Error de Validación',
            message: 'Hay problemas con los datos enviados.',
            type: 'error',
            details: errorMessage + '\n\nVerifica:\n• Todos los campos requeridos estén llenos\n• Los archivos sean del tipo correcto\n• Los formatos de fecha sean válidos'
          });
        } else if (response.status === 500) {
          setErrorModalData({
            title: '❌ Error del Servidor',
            message: 'Ocurrió un error en el servidor al procesar tu solicitud.',
            type: 'error',
            details: errorMessage + '\n\nPor favor:\n• Intenta de nuevo en unos momentos\n• Si el problema persiste, contacta al administrador\n• Guarda tu trabajo antes de reintentar'
          });
        } else {
          setErrorModalData({
            title: '❌ Error al Guardar',
            message: errorMessage,
            type: 'error',
            details: `Código de error: ${response.status}\n\nSi el problema persiste, contacta al administrador del sistema.`
          });
        }

        setShowErrorModal(true);
        console.error('Error del servidor:', errorMessage);
      }
    } catch (error) {
      console.error('Error al guardar producto:', error);

      // Error de conexión
      setErrorModalData({
        title: '🔌 Error de Conexión',
        message: 'No se pudo conectar con el servidor.',
        type: 'error',
        details: error.message + '\n\nPosibles causas:\n• Problemas de red o internet\n• El servidor no está disponible\n• La solicitud fue demasiado grande y se interrumpió\n\nRecomendaciones:\n• Verifica tu conexión a internet\n• Comprime las imágenes antes de subir\n• Intenta de nuevo en unos momentos'
      });
      setShowErrorModal(true);
    }
  };

  const handleImageChange = (numero, file) => {
    if (!file) return;

    // Validar tipo de archivo (solo imágenes)
    // Nota: image/jpg no es un MIME type estándar, el correcto es image/jpeg
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedImageTypes.includes(file.type)) {
      setErrorModalData({
        title: '⚠️ Tipo de Archivo Incorrecto',
        message: 'Este campo solo acepta archivos de imagen.',
        type: 'error',
        details: `Has intentado subir: ${file.type || 'archivo desconocido'}\n\nTipos permitidos:\n• JPEG / JPG\n• PNG\n• GIF\n• WebP\n\nPor favor selecciona un archivo de imagen válido.`
      });
      setShowErrorModal(true);
      return;
    }

    // Validar tamaño (máximo 10MB, recomendado 5MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const recommendedSize = 5 * 1024 * 1024; // 5MB

    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setErrorModalData({
        title: '❌ Imagen Demasiado Grande',
        message: 'La imagen excede el tamaño máximo permitido.',
        type: 'error',
        details: `Tamaño del archivo: ${fileSizeMB} MB\nMáximo permitido: 10 MB\n\nRecomendación:\n• Comprime la imagen antes de subirla\n• Usa herramientas como TinyPNG.com\n• Convierte PNG a JPG si es posible\n• Tamaño ideal: 3-5 MB por imagen`
      });
      setShowErrorModal(true);
      return;
    }

    // Advertencia si es mayor a 5MB pero menor a 10MB
    if (file.size > recommendedSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setErrorModalData({
        title: '⚠️ Imagen Pesada',
        message: 'La imagen es más pesada de lo recomendado.',
        type: 'warning',
        details: `Tamaño del archivo: ${fileSizeMB} MB\nRecomendado: Menos de 5 MB\n\nLa imagen se subirá, pero considera:\n• Comprimir para mejorar velocidad de carga\n• Reducir consumo de ancho de banda\n• Mejorar experiencia del usuario\n\nPuedes continuar o comprimir la imagen primero.`
      });
      setShowErrorModal(true);
      // Continuar con la carga a pesar de la advertencia
    }

    setImagenes({ ...imagenes, [`imagen_${numero}`]: file });

    // Usar URL.createObjectURL para vista previa instantánea (no congela la UI)
    const previewUrl = URL.createObjectURL(file);
    setImagenesPreviews({ ...imagenesPreviews, [`imagen_${numero}`]: previewUrl });
  };

  const handleDeleteImage = (numero) => {
    const imageKey = `imagen_${numero}`;

    // Si es una imagen existente en el servidor (del producto), marcarla para eliminar
    if (producto && producto[imageKey]) {
      setImagenesAEliminar([...imagenesAEliminar, imageKey]);
    }

    // Limpiar imagen del estado local
    setImagenes({ ...imagenes, [imageKey]: null });
    setImagenesPreviews({ ...imagenesPreviews, [imageKey]: null });
  };

  const handleDocumentoChange = (imagenNumero, tipoDocumento, file) => {
    if (!file) return;

    // Validar tipo de archivo (solo documentos)
    const allowedDocTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'application/rtf'
    ];

    if (!allowedDocTypes.includes(file.type)) {
      // Detectar si es una imagen
      const isImage = file.type.startsWith('image/');

      setErrorModalData({
        title: isImage ? '⚠️ Archivo de Imagen en Campo de Documento' : '⚠️ Tipo de Archivo No Permitido',
        message: isImage
          ? 'Has intentado subir una imagen en un campo de documentos.'
          : 'Este campo solo acepta documentos.',
        type: 'error',
        details: isImage
          ? `El archivo "${file.name}" es una imagen.\n\nEste campo es solo para documentos (PDFs, Word, Excel).\n\nPara subir imágenes:\n• Usa los campos de "Imagen 1", "Imagen 2", etc.\n• O los campos de "Certificado de Análisis" si corresponde a un lote.`
          : `Tipo de archivo: ${file.type || 'desconocido'}\n\nTipos permitidos:\n• PDF\n• Word (DOC, DOCX)\n• Excel (XLS, XLSX)\n• Texto plano (TXT)\n• RTF\n\nPor favor selecciona un documento válido.`
      });
      setShowErrorModal(true);
      return;
    }

    // Validar tamaño (máximo 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setErrorModalData({
        title: '❌ Documento Demasiado Grande',
        message: 'El documento excede el tamaño máximo permitido.',
        type: 'error',
        details: `Tamaño del archivo: ${fileSizeMB} MB\nMáximo permitido: 50 MB\n\nRecomendaciones:\n• Comprime el PDF usando herramientas online\n• Reduce la calidad de imágenes dentro del PDF\n• Divide el documento en varios archivos más pequeños\n• Usa Adobe Acrobat para optimizar el PDF`
      });
      setShowErrorModal(true);
      return;
    }

    const key = `${imagenNumero}-${tipoDocumento}`;
    setDocumentos({ ...documentos, [key]: file });
  };

  const handleDocumentoFechaChange = (imagenNumero, tipoDocumento, fecha) => {
    const key = `${imagenNumero}-${tipoDocumento}`;
    setDocumentosFechas({ ...documentosFechas, [key]: fecha });
  };

  const handleRemoveDocumento = (imagenNumero, tipoDocumento) => {
    const key = `${imagenNumero}-${tipoDocumento}`;
    const newDocumentos = { ...documentos };
    const newFechas = { ...documentosFechas };
    delete newDocumentos[key];
    delete newFechas[key];
    setDocumentos(newDocumentos);
    setDocumentosFechas(newFechas);
  };

  // Función para eliminar documento existente (guardado en el servidor)
  const handleDeleteDocumentoExistente = (imagenNumero, tipoDocumento) => {
    // Calcular el número de documento (1-48)
    const tipoDocIndex = TIPOS_DOCUMENTOS.findIndex(t => t.value === tipoDocumento);
    if (tipoDocIndex !== -1) {
      const docNumber = ((parseInt(imagenNumero) - 1) * 12) + (tipoDocIndex + 1);

      console.log('🗑️ Eliminando documento:', docNumber);

      // Agregar a la lista de documentos a eliminar
      if (!documentosAEliminar.includes(docNumber)) {
        setDocumentosAEliminar([...documentosAEliminar, docNumber]);
        console.log('✅ Documento agregado a lista de eliminación:', docNumber);
      }
    }
  };

  return (
    <>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {producto ? 'Editar Producto' : 'Nuevo Producto'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Producto Descartable */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.es_descartable}
                  onChange={(e) => setFormData({ ...formData, es_descartable: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Producto Descartable
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Si el producto es descartable, no requiere registro sanitario
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Registro Sanitario */}
          {!formData.es_descartable && (
            <Grid item xs={12}>
              <Autocomplete
                options={registrosSanitarios}
                value={selectedRegistro}
                onChange={handleRegistroChange}
                getOptionLabel={(option) => `${option.numero_registro} - ${option.producto_nombre}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Registro Sanitario (PF)"
                    placeholder="Selecciona un registro sanitario"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.numero_registro}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.producto_nombre} - {option.estado}
                      </Typography>
                    </Box>
                  </li>
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
              />
              {selectedRegistro && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Chip
                    label={`Estado: ${selectedRegistro.estado}`}
                    size="small"
                    color={selectedRegistro.estado === 'vigente' ? 'success' : 'warning'}
                  />
                  <Chip
                    label={`Vence: ${new Date(selectedRegistro.fecha_vencimiento).toLocaleDateString()}`}
                    size="small"
                  />
                </Box>
              )}
            </Grid>
          )}

          {/* Nombre */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre del Producto *"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              helperText="Campo obligatorio"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>

          {/* Código SAP */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Código SAP"
              value={formData.codigo_sap}
              onChange={(e) => setFormData({ ...formData, codigo_sap: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>

          {/* Descripción de la Presentación */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Descripción de la Presentación"
              value={formData.descripcion_presentacion}
              onChange={(e) => setFormData({ ...formData, descripcion_presentacion: e.target.value })}
              placeholder="Ej: Caja con 20 tabletas de 500mg"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>

          {/* Laboratorio Fabricante */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Laboratorio Fabricante"
              value={formData.laboratorio_fabricante}
              onChange={(e) => setFormData({ ...formData, laboratorio_fabricante: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>

          {/* Categoría */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Categoría del Producto"
              value={formData.categoria}
              onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
              SelectProps={{ native: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            >
              <option value="Controlado">Controlado</option>
              <option value="No controlado">No controlado</option>
              <option value="Descartable">Descartable</option>
            </TextField>
          </Grid>

          {/* Vida Útil */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Vida Útil"
              value={formData.vida_util}
              onChange={(e) => setFormData({ ...formData, vida_util: e.target.value })}
              placeholder="Ej: 36 meses"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>

          {/* País de Origen */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="País de Origen"
              value={formData.pais_origen}
              onChange={(e) => setFormData({ ...formData, pais_origen: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>

          {/* Código del Proveedor */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Código del Proveedor"
              value={formData.codigo_proveedor}
              onChange={(e) => setFormData({ ...formData, codigo_proveedor: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>

          {/* Estado */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Estado"
              value={formData.estado}
              onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              SelectProps={{ native: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
              <option value="descontinuado">Descontinuado</option>
            </TextField>
          </Grid>

          {/* Imágenes del Producto con Lotes */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
              Imágenes del Producto (4 lotes máximo)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Para cada imagen puedes agregar: número de lote, fecha de vencimiento y certificado de análisis (PDF)
            </Typography>
          </Grid>

          {[1, 2, 3, 4].map((numero) => (
            <Grid item xs={12} md={6} key={numero}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #e0e0e0' }}>
                <CardContent>
                  {/* Sección de Imagen */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Imagen {numero}
                  </Typography>
                  <Box
                    sx={{
                      border: '2px dashed #cbd5e1',
                      borderRadius: '12px',
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      mb: 3,
                      '&:hover': {
                        borderColor: '#3b82f6',
                        bgcolor: 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  >
                    {imagenesPreviews[`imagen_${numero}`] ? (
                      <Box>
                        <Box
                          component="img"
                          src={imagenesPreviews[`imagen_${numero}`]}
                          alt={`Imagen ${numero}`}
                          sx={{
                            width: '100%',
                            height: 150,
                            objectFit: 'cover',
                            borderRadius: '8px',
                            mb: 1,
                          }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteImage(numero)}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id={`imagen-${numero}`}
                          type="file"
                          onChange={(e) => handleImageChange(numero, e.target.files[0])}
                        />
                        <label htmlFor={`imagen-${numero}`}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                bgcolor: 'rgba(59, 130, 246, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1,
                              }}
                            >
                              <Typography variant="h6" color="primary">
                                {numero}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Click para subir imagen
                            </Typography>
                          </Box>
                        </label>
                      </>
                    )}
                  </Box>

                  {/* Campos de Lote */}
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <TextField
                      fullWidth
                      label={`Número de Lote ${numero}`}
                      value={lotes[`lote_${numero}`]}
                      onChange={(e) => setLotes({ ...lotes, [`lote_${numero}`]: e.target.value })}
                      size="small"
                      placeholder="Ej: LOT-2024-001"
                      sx={{ mb: 1.5 }}
                    />
                    <TextField
                      fullWidth
                      label={`Fecha de Vencimiento del Lote ${numero}`}
                      type="date"
                      value={loteFechas[`fecha_lote_${numero}`]}
                      onChange={(e) => setLoteFechas({ ...loteFechas, [`fecha_lote_${numero}`]: e.target.value })}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 1.5 }}
                    />

                    {/* Certificado de Análisis */}
                    <Box sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      p: 1.5,
                      bgcolor: certificadosAnalisis[`cert_${numero}`] || (producto && producto[`lot${numero}AnalysisCert`]) ? 'rgba(76, 175, 80, 0.05)' : 'transparent'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                        Certificado de Análisis (PDF)
                      </Typography>

                      {certificadosAnalisis[`cert_${numero}`] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PictureAsPdf sx={{ fontSize: 18, color: '#4caf50' }} />
                          <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem' }}>
                            {certificadosAnalisis[`cert_${numero}`].name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setCertificadosAnalisis({ ...certificadosAnalisis, [`cert_${numero}`]: null })}
                            sx={{ p: 0.5 }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : producto && producto[`lot${numero}AnalysisCert`] && !certificadosEliminados[`cert_${numero}`] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PictureAsPdf sx={{ fontSize: 18, color: '#4caf50' }} />
                          <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem', color: '#4caf50' }}>
                            {producto[`lot${numero}AnalysisCertName`] || producto[`lot${numero}AnalysisCert`].split('/').pop() || 'Certificado existente'}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setCertificadosEliminados({ ...certificadosEliminados, [`cert_${numero}`]: true })}
                            sx={{ p: 0.5 }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <input
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            id={`cert-${numero}`}
                            type="file"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                setCertificadosAnalisis({ ...certificadosAnalisis, [`cert_${numero}`]: e.target.files[0] });
                              }
                            }}
                          />
                          <label htmlFor={`cert-${numero}`}>
                            <Button
                              variant="outlined"
                              component="span"
                              size="small"
                              fullWidth
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              Subir Certificado PDF
                            </Button>
                          </label>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* NOTA: Los documentos ahora son GLOBALES (ver abajo del loop de imágenes) */}
                  {/* Esta sección de documentos se eliminará en una versión futura */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, mt: 3, display: 'none' }}>
                    Documentos del Lote {numero} (12 campos)
                  </Typography>
                  <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1, display: 'none' }}>
                    {TIPOS_DOCUMENTOS.map((tipoDoc, index) => {
                      const key = `${numero}-${tipoDoc.value}`;
                      const documento = documentos[key];
                      const fechaVencimiento = documentosFechas[key] || '';

                      // Calcular el número de documento en la BD (1-48)
                      const docNumber = ((numero - 1) * 12) + (index + 1);
                      // Verificar si el documento existe Y no está marcado para eliminar
                      const estaEnListaEliminacion = documentosAEliminar.includes(docNumber);
                      const documentoExistente = (producto && producto[`document${docNumber}`] && !estaEnListaEliminacion) ? producto[`document${docNumber}`] : null;
                      const nombreDocumentoExistente = (producto && !estaEnListaEliminacion) ? producto[`documentName${docNumber}`] : null;

                      return (
                        <Box
                          key={tipoDoc.value}
                          sx={{
                            p: 2,
                            mb: 2,
                            border: '1px solid',
                            borderColor: (documento || documentoExistente) ? '#4caf50' : '#e0e0e0',
                            borderRadius: '8px',
                            bgcolor: (documento || documentoExistente) ? 'rgba(76, 175, 80, 0.05)' : 'transparent',
                            transition: 'all 0.3s',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              {tipoDoc.label}
                            </Typography>
                            {tipoDoc.requiresExpiry && (
                              <Chip
                                label="Vencimiento requerido"
                                size="small"
                                color="warning"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>

                          {documento ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PictureAsPdf sx={{ fontSize: 20, color: '#4caf50' }} />
                                <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem', color: '#4caf50' }}>
                                  {documento.name}
                                </Typography>
                                <Chip
                                  label="Nuevo"
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                                {fechaVencimiento && (
                                  <Chip
                                    label={fechaVencimiento}
                                    size="small"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveDocumento(numero, tipoDoc.value)}
                                  sx={{ p: 0.5 }}
                                >
                                  <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                              {documentoExistente && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                  ⚠️ Reemplazará: {nombreDocumentoExistente || 'Documento existente'}
                                </Typography>
                              )}
                            </Box>
                          ) : documentoExistente ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PictureAsPdf sx={{ fontSize: 20, color: '#1976d2' }} />
                                <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem', color: '#1976d2' }}>
                                  {nombreDocumentoExistente || 'Documento existente'}
                                </Typography>
                                <Chip
                                  label="Guardado"
                                  size="small"
                                  color="info"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDocumentoExistente(numero, tipoDoc.value)}
                                  sx={{ p: 0.5, color: '#d32f2f' }}
                                  title="Eliminar documento"
                                >
                                  <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 1 }}>
                                Sube un nuevo archivo para reemplazarlo
                              </Typography>
                              {tipoDoc.requiresExpiry && (
                                <TextField
                                  type="date"
                                  label="Fecha de Vencimiento"
                                  size="small"
                                  fullWidth
                                  value={fechaVencimiento}
                                  onChange={(e) => handleDocumentoFechaChange(numero, tipoDoc.value, e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ mb: 1, fontSize: '0.8rem' }}
                                />
                              )}
                              <input
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                style={{ display: 'none' }}
                                id={`doc-${numero}-${tipoDoc.value}`}
                                type="file"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleDocumentoChange(numero, tipoDoc.value, file);
                                  }
                                }}
                              />
                              <label htmlFor={`doc-${numero}-${tipoDoc.value}`}>
                                <Button
                                  component="span"
                                  size="small"
                                  startIcon={<CloudUpload />}
                                  fullWidth
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                  }}
                                >
                                  Reemplazar documento
                                </Button>
                              </label>

                              {/* Mostrar nuevo archivo seleccionado */}
                              {documentos[`${numero}-${tipoDoc.value}`] && (
                                <Box sx={{ mt: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1,
                                      border: '2px solid #4caf50',
                                      borderRadius: '8px',
                                      backgroundColor: '#e8f5e9'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                      <PictureAsPdf sx={{ color: '#4caf50', mr: 1, flexShrink: 0 }} />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#2e7d32',
                                          fontWeight: 600,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          flex: 1
                                        }}
                                      >
                                        {documentos[`${numero}-${tipoDoc.value}`].name}
                                      </Typography>
                                      <Chip
                                        label="Nuevo"
                                        size="small"
                                        sx={{
                                          ml: 1,
                                          backgroundColor: '#4caf50',
                                          color: 'white',
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          height: '20px',
                                          flexShrink: 0
                                        }}
                                      />
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveDocumento(numero, tipoDoc.value)}
                                      sx={{
                                        ml: 1,
                                        color: '#d32f2f',
                                        flexShrink: 0
                                      }}
                                    >
                                      <Close fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      mt: 0.5,
                                      display: 'block',
                                      color: '#ed6c02',
                                      fontWeight: 600
                                    }}
                                  >
                                    ⚠️ Reemplazará: {nombreDocumentoExistente || 'Documento existente'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box>
                              {tipoDoc.requiresExpiry && (
                                <TextField
                                  type="date"
                                  label="Fecha de Vencimiento"
                                  size="small"
                                  fullWidth
                                  value={fechaVencimiento}
                                  onChange={(e) => handleDocumentoFechaChange(numero, tipoDoc.value, e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ mb: 1, fontSize: '0.8rem' }}
                                />
                              )}
                              <input
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                style={{ display: 'none' }}
                                id={`doc-${numero}-${tipoDoc.value}`}
                                type="file"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleDocumentoChange(numero, tipoDoc.value, file);
                                  }
                                }}
                              />
                              <label htmlFor={`doc-${numero}-${tipoDoc.value}`}>
                                <Button
                                  component="span"
                                  size="small"
                                  startIcon={<CloudUpload />}
                                  fullWidth
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                  }}
                                >
                                  Subir documento (máx 10MB)
                                </Button>
                              </label>

                              {/* Mostrar archivo seleccionado */}
                              {documentos[`${numero}-${tipoDoc.value}`] && (
                                <Box sx={{ mt: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1,
                                      border: '2px solid #4caf50',
                                      borderRadius: '8px',
                                      backgroundColor: '#e8f5e9'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                      <PictureAsPdf sx={{ color: '#4caf50', mr: 1, flexShrink: 0 }} />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#2e7d32',
                                          fontWeight: 600,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          flex: 1
                                        }}
                                      >
                                        {documentos[`${numero}-${tipoDoc.value}`].name}
                                      </Typography>
                                      <Chip
                                        label="Nuevo"
                                        size="small"
                                        sx={{
                                          ml: 1,
                                          backgroundColor: '#4caf50',
                                          color: 'white',
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          height: '20px',
                                          flexShrink: 0
                                        }}
                                      />
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveDocumento(numero, tipoDoc.value)}
                                      sx={{
                                        ml: 1,
                                        color: '#d32f2f',
                                        flexShrink: 0
                                      }}
                                    >
                                      <Close fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Documentos Globales (12 campos compartidos entre las 4 imágenes) */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
              Documentos
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ maxHeight: 600, overflowY: 'auto', pr: 1 }}>
              {TIPOS_DOCUMENTOS.map((tipoDoc, index) => {
                const docNumber = index + 1; // 1-12
                const key = `global-${tipoDoc.value}`;
                const documento = documentos[key];
                const fechaVencimiento = documentosFechas[key] || '';

                // Verificar si el documento existe Y no está marcado para eliminar
                const estaEnListaEliminacion = documentosAEliminar.includes(docNumber);
                const documentoExistente = (producto && producto[`document${docNumber}`] && !estaEnListaEliminacion) ? producto[`document${docNumber}`] : null;
                const nombreDocumentoExistente = (producto && !estaEnListaEliminacion) ? producto[`documentName${docNumber}`] : null;

                return (
                  <Box
                    key={tipoDoc.value}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid',
                      borderColor: (documento || documentoExistente) ? '#4caf50' : '#e0e0e0',
                      borderRadius: '8px',
                      bgcolor: (documento || documentoExistente) ? 'rgba(76, 175, 80, 0.05)' : 'transparent',
                      transition: 'all 0.3s',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Documento {docNumber}
                      </Typography>
                    </Box>

                    {documento ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PictureAsPdf sx={{ fontSize: 20, color: '#4caf50' }} />
                          <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem', color: '#4caf50' }}>
                            {documento.name}
                          </Typography>
                          <Chip
                            label="Nuevo"
                            size="small"
                            color="success"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newDocs = { ...documentos };
                              delete newDocs[key];
                              setDocumentos(newDocs);
                              const newFechas = { ...documentosFechas };
                              delete newFechas[key];
                              setDocumentosFechas(newFechas);
                            }}
                            sx={{ p: 0.5 }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : documentoExistente ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PictureAsPdf sx={{ fontSize: 20, color: '#1976d2' }} />
                          <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem', color: '#1976d2' }}>
                            {nombreDocumentoExistente || 'Documento existente'}
                          </Typography>
                          <Chip
                            label="Guardado"
                            size="small"
                            color="info"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDocumentosAEliminar([...documentosAEliminar, docNumber]);
                            }}
                            sx={{ p: 0.5, color: '#d32f2f' }}
                            title="Eliminar documento"
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <input
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/rtf"
                          style={{ display: 'none' }}
                          id={`global-doc-${docNumber}`}
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              // Validar tipo de archivo
                              const allowedDocTypes = [
                                'application/pdf',
                                'application/msword',
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                'application/vnd.ms-excel',
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                'text/plain',
                                'application/rtf'
                              ];

                              if (!allowedDocTypes.includes(file.type)) {
                                setErrorModalData({
                                  title: '⚠️ Tipo de Archivo No Permitido',
                                  message: 'Este campo solo acepta documentos.',
                                  type: 'error',
                                  details: `Tipo de archivo: ${file.type || 'desconocido'}\n\nTipos permitidos:\n• PDF\n• Word (DOC, DOCX)\n• Excel (XLS, XLSX)\n• Texto plano (TXT)\n• RTF`
                                });
                                setShowErrorModal(true);
                                e.target.value = ''; // Limpiar el input
                                return;
                              }

                              // Validar tamaño (máximo 50MB)
                              const maxSize = 50 * 1024 * 1024;
                              if (file.size > maxSize) {
                                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                                setErrorModalData({
                                  title: '❌ Documento Demasiado Grande',
                                  message: 'El documento excede el tamaño máximo permitido.',
                                  type: 'error',
                                  details: `Tamaño del archivo: ${fileSizeMB} MB\nMáximo permitido: 50 MB`
                                });
                                setShowErrorModal(true);
                                e.target.value = '';
                                return;
                              }

                              setDocumentos({ ...documentos, [key]: file });
                            }
                          }}
                        />
                        <label htmlFor={`global-doc-${docNumber}`}>
                          <Button
                            variant="outlined"
                            component="span"
                            size="small"
                            fullWidth
                            startIcon={<PictureAsPdf />}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Subir Documento {docNumber}
                          </Button>
                        </label>
                      </>
                    )}

                    {(documento || documentoExistente) && (
                      <TextField
                        fullWidth
                        type="date"
                        label="Fecha de vencimiento (opcional)"
                        value={fechaVencimiento}
                        onChange={(e) => setDocumentosFechas({ ...documentosFechas, [key]: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          startIcon={<Cancel />}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<Save />}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
          }}
        >
          {producto ? 'Actualizar' : 'Crear'} Producto
        </Button>
      </DialogActions>

      {/* Modal de Éxito Super Cool */}
      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successModalData.title}
        message={successModalData.message}
        type={successModalData.type}
      />

      {/* Modal de Errores */}
      <ErrorModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
        type={errorModalData.type}
        details={errorModalData.details}
      />
    </>
  );
};

export default ProductoForm;
